"use client";

import { Button, Form, Input } from "antd";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import * as yup from "yup";
import { useEffect } from "react";
import { WebInfoType } from "@src/types/api/webInfo";
import {
  useWebInfoDetail,
  useWebInfoSave,
} from "../../../hooks/api/web-info/hooks";

const schema = yup.object({
  faqs: yup
    .array()
    .of(
      yup.object({
        qns: yup.string().required("Question is required"),
        ans: yup.string().required("Answer is required"),
      })
    )
    .min(1, "At least one FAQ is required"),
});

type FAQFormType = yup.InferType<typeof schema>;

const FaqForm = ({ type }: { type: WebInfoType }) => {
  const { data: details } = useWebInfoDetail(type);

  const { mutate: saveForm, isPending } = useWebInfoSave({
    onSuccess: () => toast.success("Saved successfully"),
    onError: (error: any) =>
      toast.error(error?.message || "Something went wrong"),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FAQFormType>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      faqs: [{ qns: "", ans: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "faqs",
    control,
  });

  // Prefill old data
  useEffect(() => {
    if (details?.data?.faqs) {
      reset({
        faqs: details.data.faqs.length
          ? details.data.faqs
          : [{ qns: "", ans: "" }],
      });
    }
  }, [details?.data, reset]);

  const onSubmit = (values: FAQFormType) => {
    saveForm({
      ...(details?.data?._id && { _id: details.data._id }),
      type,
      faqs: values.faqs,
    });
  };

  return (
    <div className="w-full mx-auto">
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <div className="p-6 bg-[#fdf0e3] rounded-xl space-y-6">
          <h2 className="font-semibold text-lg">FAQs</h2>

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="border p-4 rounded-lg bg-white shadow-sm space-y-4"
            >
              {/* Question */}
              <Form.Item
                label={`Question ${index + 1}`}
                validateStatus={errors.faqs?.[index]?.qns ? "error" : ""}
                help={errors.faqs?.[index]?.qns?.message}
              >
                <Controller
                  name={`faqs.${index}.qns`}
                  control={control}
                  render={({ field }) => (
                    <Input {...field} placeholder="Enter question" />
                  )}
                />
              </Form.Item>

              {/* Answer */}
              <Form.Item
                label="Answer"
                validateStatus={errors.faqs?.[index]?.ans ? "error" : ""}
                help={errors.faqs?.[index]?.ans?.message}
              >
                <Controller
                  name={`faqs.${index}.ans`}
                  control={control}
                  render={({ field }) => (
                    <Input.TextArea
                      {...field}
                      placeholder="Enter answer"
                      rows={3}
                    />
                  )}
                />
              </Form.Item>

              {/* Remove Button */}
              {fields.length > 1 && (
                <Button danger onClick={() => remove(index)}>
                  Remove FAQ
                </Button>
              )}
            </div>
          ))}

          {/* Add FAQ Button */}
          <Button
            type="dashed"
            onClick={() => append({ qns: "", ans: "" })}
            className="w-full"
          >
            + Add FAQ
          </Button>
        </div>

        {/* Submit Button */}
        <div className="mt-6 flex justify-start">
          <Button
            type="primary"
            htmlType="submit"
            loading={isPending}
            className="bg-primary px-10"
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default FaqForm;
